import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Briefcase, Building2, Users,
  Settings, Shield, Loader2, TrendingUp, Clock, CheckCircle,
  AlertCircle, ChevronRight, MessageSquare, Globe, Tag
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabase';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { language } = useLanguage();

  const [userRole, setUserRole] = useState(null);
  const [stats, setStats] = useState({
    quotes: { total: 0, new: 0 },
    services: { total: 0, active: 0 },
    jobs: { total: 0, applications: 0 },
    references: { total: 0, featured: 0 },
    contacts: { total: 0, unread: 0 },
    promoCodes: { total: 0, active: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/auth/login');
        return;
      }
      checkAdminRole();
    }
  }, [user, authLoading, navigate]);

  const checkAdminRole = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      const role = profile?.role || 'customer';
      setUserRole(role);

      if (role === 'admin' || role === 'editor') {
        loadStats();
      }
    } catch (err) {
      console.error('Role check error:', err);
      setUserRole('customer');
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      // Load quotes stats
      const { data: quotes } = await supabase.from('quote_requests').select('status');
      const newQuotes = quotes?.filter(q => q.status === 'new').length || 0;

      // Load services stats
      const { data: services } = await supabase.from('services').select('is_active');
      const activeServices = services?.filter(s => s.is_active).length || 0;

      // Load jobs stats
      const { data: jobs } = await supabase.from('career_jobs').select('id');
      const { data: applications } = await supabase.from('career_applications').select('id');

      // Load references stats
      const { data: refs } = await supabase.from('our_references').select('is_featured');
      const featuredRefs = refs?.filter(r => r.is_featured).length || 0;

      // Load contact messages stats
      const { data: contacts } = await supabase.from('contact_messages').select('is_read');
      const unreadContacts = contacts?.filter(c => !c.is_read).length || 0;

      // Load promo codes stats
      const { data: promoCodes } = await supabase.from('promo_codes').select('is_active');
      const activePromoCodes = promoCodes?.filter(p => p.is_active).length || 0;

      setStats({
        quotes: { total: quotes?.length || 0, new: newQuotes },
        services: { total: services?.length || 0, active: activeServices },
        jobs: { total: jobs?.length || 0, applications: applications?.length || 0 },
        references: { total: refs?.length || 0, featured: featuredRefs },
        contacts: { total: contacts?.length || 0, unread: unreadContacts },
        promoCodes: { total: promoCodes?.length || 0, active: activePromoCodes }
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const texts = {
    tr: {
      title: 'Admin Paneli',
      welcome: 'Hoş Geldiniz',
      overview: 'Genel Bakış',
      quickActions: 'Hızlı Erişim',
      quotes: 'Teklif Talepleri',
      quotesDesc: 'Müşteri tekliflerini yönetin',
      services: 'Hizmetler',
      servicesDesc: 'Çözümlerinizi düzenleyin',
      careers: 'Kariyer',
      careersDesc: 'İş ilanları ve başvurular',
      references: 'Referanslar',
      referencesDesc: 'Proje portföyünüz',
      contacts: 'İletişim',
      contactsDesc: 'Gelen mesajlar',
      promoCodes: 'Promosyon Kodları',
      promoCodesDesc: 'İndirim kodlarını yönetin',
      settings: 'Ayarlar',
      settingsDesc: 'Site yapılandırması',
      total: 'Toplam',
      new: 'Yeni',
      active: 'Aktif',
      featured: 'Öne Çıkan',
      applications: 'Başvuru',
      unread: 'Okunmamış',
      recentActivity: 'Son Aktiviteler',
      viewAll: 'Tümünü Gör'
    },
    en: {
      title: 'Admin Panel',
      welcome: 'Welcome',
      overview: 'Overview',
      quickActions: 'Quick Actions',
      quotes: 'Quote Requests',
      quotesDesc: 'Manage customer quotes',
      services: 'Services',
      servicesDesc: 'Edit your solutions',
      careers: 'Careers',
      careersDesc: 'Job listings & applications',
      references: 'References',
      referencesDesc: 'Your project portfolio',
      contacts: 'Contact',
      contactsDesc: 'Incoming messages',
      promoCodes: 'Promo Codes',
      promoCodesDesc: 'Manage discount codes',
      settings: 'Settings',
      settingsDesc: 'Site configuration',
      total: 'Total',
      new: 'New',
      active: 'Active',
      featured: 'Featured',
      applications: 'Applications',
      unread: 'Unread',
      recentActivity: 'Recent Activity',
      viewAll: 'View All'
    }
  };

  const t = texts[language] || texts.tr;

  const menuItems = [
    {
      id: 'quotes',
      title: t.quotes,
      description: t.quotesDesc,
      icon: FileText,
      href: '/admin/quotes',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      stats: [
        { label: t.total, value: stats.quotes.total },
        { label: t.new, value: stats.quotes.new, highlight: stats.quotes.new > 0 }
      ]
    },
    {
      id: 'services',
      title: t.services,
      description: t.servicesDesc,
      icon: Settings,
      href: '/admin/services',
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      stats: [
        { label: t.total, value: stats.services.total },
        { label: t.active, value: stats.services.active }
      ]
    },
    {
      id: 'careers',
      title: t.careers,
      description: t.careersDesc,
      icon: Briefcase,
      href: '/admin/career-jobs',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      stats: [
        { label: t.total, value: stats.jobs.total },
        { label: t.applications, value: stats.jobs.applications }
      ]
    },
    {
      id: 'references',
      title: t.references,
      description: t.referencesDesc,
      icon: Building2,
      href: '/admin/references',
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
      stats: [
        { label: t.total, value: stats.references.total },
        { label: t.featured, value: stats.references.featured }
      ]
    },
    {
      id: 'contacts',
      title: t.contacts,
      description: t.contactsDesc,
      icon: MessageSquare,
      href: '/admin/contacts',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      stats: [
        { label: t.total, value: stats.contacts.total },
        { label: t.unread, value: stats.contacts.unread, highlight: stats.contacts.unread > 0 }
      ]
    },
    {
      id: 'promo-codes',
      title: t.promoCodes,
      description: t.promoCodesDesc,
      icon: Tag,
      href: '/admin/promo-codes',
      color: 'from-pink-500 to-rose-600',
      bgColor: 'bg-pink-50 dark:bg-pink-900/20',
      stats: [
        { label: t.total, value: stats.promoCodes.total },
        { label: t.active, value: stats.promoCodes.active }
      ]
    }
  ];

  // Loading state
  if (authLoading || (user && userRole === null)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  // Access denied
  if (userRole && userRole !== 'admin' && userRole !== 'editor') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {language === 'tr' ? 'Erişim Reddedildi' : 'Access Denied'}
          </h1>
          <button onClick={() => navigate('/')} className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-xl">
            {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400">
              {language === 'tr' ? 'Ana Sayfa' : 'Home'}
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-900 dark:text-white font-medium">{t.title}</span>
          </nav>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <LayoutDashboard className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t.title}</h1>
                <p className="text-gray-500 dark:text-gray-400">{t.welcome}, {user?.email?.split('@')[0]}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm font-medium rounded-full capitalize">
                {userRole}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.quotes.total}</p>
                <p className="text-sm text-gray-500">{t.quotes}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Settings className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.services.active}</p>
                <p className="text-sm text-gray-500">{t.services}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.jobs.applications}</p>
                <p className="text-sm text-gray-500">{t.applications}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <MessageSquare className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.contacts.unread}</p>
                <p className="text-sm text-gray-500">{t.unread}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t.quickActions}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {menuItems.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Link
                  to={item.href}
                  className="block group"
                  data-testid={`admin-menu-${item.id}`}
                >
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 group-hover:translate-x-1 transition-all" />
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      {item.description}
                    </p>

                    <div className="flex items-center gap-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                      {item.stats.map((stat, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className={`text-lg font-bold ${stat.highlight ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>
                            {stat.value}
                          </span>
                          <span className="text-xs text-gray-500">{stat.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Links */}
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-white mb-1">
                {language === 'tr' ? 'Siteyi Görüntüle' : 'View Site'}
              </h3>
              <p className="text-blue-100">
                {language === 'tr' ? 'Yaptığınız değişiklikleri kontrol edin' : 'Check your changes live'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors"
              >
                <Globe className="w-4 h-4 mr-2" />
                {language === 'tr' ? 'Ana Sayfa' : 'Homepage'}
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-lg hover:shadow-lg transition-all"
              >
                {language === 'tr' ? 'Çözümler' : 'Solutions'}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
